/**
 * @fileoverview Progress Photo Upload Modal Component
 *
 * Modal for uploading construction progress photos to the Files & Photos
 * table in Airtable. Photos are automatically categorized as
 * "Construction Progress" with type "Progress Photo".
 *
 * Features:
 * - Project selector (prefilled and disabled when projectId is passed)
 * - Optional task linking
 * - Multiple photo upload with drag-and-drop
 * - Image preview thumbnails
 * - Upload progress tracking
 * - Notes field for descriptions
 *
 * Upload Flow:
 * 1. User selects project (or uses prefilled project)
 * 2. User optionally selects a task to link photos to
 * 3. User selects/drops image files
 * 4. Files are uploaded to Cloudinary
 * 5. Cloudinary URLs are sent to Firebase function
 * 6. Firebase function creates records in Files & Photos table
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../../services/cloudinaryService
 * @requires ../../firebase
 * @version 1.0.0
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaImage,
  FaUpload,
  FaSpinner,
  FaCheckCircle,
  FaExclamationCircle,
  FaTrash,
  FaHardHat,
  FaTasks,
  FaEdit,
  FaHome,
  FaStickyNote,
  FaCamera,
} from "react-icons/fa";
import * as Sentry from "@sentry/react";
import {
  uploadFile,
  buildUploadContext,
} from "../../services/cloudinaryService";
import { functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import useCurrentTeamMember from "../../hooks/useCurrentTeamMember";
import ConfirmationModal from "../ConfirmationModal";
import "./ProgressPhotoUploadModal.css";

/**
 * Allowed image file types for progress photos
 * @constant {Object}
 */
const ALLOWED_IMAGE_TYPES = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
};

/**
 * Maximum file size in bytes (10MB)
 * @constant {number}
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Maximum number of files per upload batch
 * @constant {number}
 */
const MAX_FILES = 20;

/**
 * Upload status enum
 * @enum {string}
 */
const UploadStatus = {
  PENDING: "pending",
  UPLOADING: "uploading",
  SUCCESS: "success",
  ERROR: "error",
};

/**
 * Construction phase options
 * @constant {Array<Object>}
 */
const CONSTRUCTION_PHASES = [
  { value: "Planning/Permitting", label: "Planning/Permitting" },
  { value: "Foundation", label: "Foundation" },
  { value: "Framing", label: "Framing" },
  { value: "Rough-In", label: "Rough-In" },
  { value: "Insulation & Drywall", label: "Insulation & Drywall" },
  { value: "Trim & Paint", label: "Trim & Paint" },
  { value: "Mechanical Trim", label: "Mechanical Trim" },
  { value: "Final Finishes", label: "Final Finishes" },
  { value: "Punch List", label: "Punch List" },
  { value: "Final Inspection / CO", label: "Final Inspection / CO" },
  { value: "Complete", label: "Complete" },
];

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validate an image file
 * @param {File} file - The file to validate
 * @returns {Object} Validation result with isValid and error
 */
const validateImageFile = (file) => {
  // Check file type
  if (!Object.keys(ALLOWED_IMAGE_TYPES).includes(file.type)) {
    return {
      isValid: false,
      error: `Invalid file type: ${file.type}. Only images are allowed.`,
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File too large: ${formatFileSize(file.size)}. Maximum is 10MB.`,
    };
  }

  return { isValid: true, error: null };
};

/**
 * Progress Photo Upload Modal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} [props.mode] - Modal mode: "create" (default) or "edit"
 * @param {Object} [props.existingPhoto] - Existing photo data (required if mode="edit")
 * @param {string} [props.projectId] - Pre-selected project ID (disables selector)
 * @param {string} [props.projectName] - Pre-selected project name for display
 * @param {Object} [props.project] - Full project object (for defaulting phase)
 * @param {Array} [props.projects] - Array of projects for selector
 * @param {Function} [props.onUploadComplete] - Callback after successful upload
 * @param {Function} [props.onDelete] - Callback after successful deletion
 * @returns {JSX.Element|null} The modal component or null if closed
 */
const ProgressPhotoUploadModal = ({
  isOpen,
  onClose,
  mode = "create",
  existingPhoto = null,
  projectId: initialProjectId,
  projectName: initialProjectName,
  project = null,
  projects = [],
  onUploadComplete,
  onDelete,
  showTestAccounts = false,
}) => {
  // ==================== STATE ====================

  // Mode detection
  const isEditMode = mode === "edit" && existingPhoto;

  // Canonical RSH team member name for Cloudinary `uploaded_by_name`
  const { name: teamMemberName } = useCurrentTeamMember();

  // Project selection
  const [selectedProjectId, setSelectedProjectId] = useState(
    initialProjectId || "",
  );
  const [selectedProjectName, setSelectedProjectName] = useState(
    initialProjectName || "",
  );
  const isProjectLocked = Boolean(initialProjectId);

  // Task selection (optional)
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Photo naming (NEW)
  const [photoName, setPhotoName] = useState("");
  const [individualNames, setIndividualNames] = useState(false);
  const [fileNames, setFileNames] = useState({});

  // Construction phase (NEW)
  const [selectedPhase, setSelectedPhase] = useState("");

  // File management
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Form fields
  const [notes, setNotes] = useState("");

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [error, setError] = useState("");

  // Edit/Delete state (NEW)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ==================== EFFECTS ====================

  /**
   * Lock body scroll when modal is open
   */
  /*useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [isOpen]);

  /**
   * Fetch active projects when modal opens (if project not locked)
   */
  const [internalProjects, setInternalProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !isProjectLocked) {
      const fetchProjects = async () => {
        setProjectsLoading(true);
        try {
          const getActiveProjects = httpsCallable(
            functions,
            "getActiveProjects",
          );
          const result = await getActiveProjects({ showTestAccounts });
          if (result.data?.success && result.data?.data) {
            setInternalProjects(result.data.data);
          }
        } catch (err) {
          console.error("Error fetching projects:", err);
          Sentry.captureException(err);
        } finally {
          setProjectsLoading(false);
        }
      };
      fetchProjects();
    }
  }, [isOpen, isProjectLocked, showTestAccounts]);

  /**
   * Initialize fields in edit mode or default phase from project
   */
  useEffect(() => {
    if (isEditMode) {
      // Edit mode: populate from existing photo
      setPhotoName(existingPhoto.fileName || "");
      setSelectedPhase(existingPhoto.phase || "");
      setNotes(existingPhoto.notes || "");
      setSelectedProjectId(existingPhoto.projectId || initialProjectId || "");
      setSelectedTaskId(existingPhoto.taskId || "");
    } else {
      // Create mode: default phase from project if available
      if (project?.phase) {
        setSelectedPhase(project.phase);
      }
    }
  }, [isEditMode, existingPhoto, project, initialProjectId]);

  /**
   * Sync project selection from props (create mode only). Bidirectional:
   * applies the parent's value when supplied, and clears when the parent
   * removes it — e.g., AdminUploads' top selector switching back to
   * "All projects". useState only initializes once at mount, so this is
   * what makes the locked-name display update when the prop arrives after
   * mount and the dropdown reset when the prop is cleared.
   */
  useEffect(() => {
    if (isEditMode) return;
    setSelectedProjectId(initialProjectId || "");
    setSelectedProjectName(initialProjectName || "");
  }, [initialProjectId, initialProjectName, isEditMode]);

  /**
   * Fetch tasks when project is selected
   */
  useEffect(() => {
    const fetchTasks = async () => {
      if (!selectedProjectId) {
        setTasks([]);
        return;
      }

      setLoadingTasks(true);
      try {
        const getTasks = httpsCallable(functions, "getTasks");
        const result = await getTasks({ projectId: selectedProjectId });

        if (result.data?.success && result.data?.data) {
          // Sort tasks by phase order and name
          const sortedTasks = result.data.data.sort((a, b) => {
            // First by phase order if available
            if (a.phaseOrder && b.phaseOrder) {
              if (a.phaseOrder !== b.phaseOrder) {
                return a.phaseOrder - b.phaseOrder;
              }
            }
            // Then by task name
            return (a.taskName || "").localeCompare(b.taskName || "");
          });
          setTasks(sortedTasks);
        }
      } catch (err) {
        console.error("Error fetching tasks:", err);
        Sentry.captureException(err);
        // Non-blocking - tasks are optional
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
  }, [selectedProjectId]);

  /**
   * Reset form when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow animation
      const timer = setTimeout(() => {
        setInternalProjects([]);
        setFiles([]);
        setNotes("");
        setSelectedTaskId("");
        setOverallProgress(0);
        setUploadComplete(false);
        setUploadResults([]);
        setError("");
        if (!isProjectLocked) {
          setSelectedProjectId("");
          setSelectedProjectName("");
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isProjectLocked]);

  // ==================== HANDLERS ====================

  /**
   * Handle project selection change
   * @param {React.ChangeEvent} e - Change event
   */
  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProjectId(projectId);
    setSelectedTaskId(""); // Reset task when project changes

    // Find project name from internal (fetched) or prop-provided list
    const allProjects =
      internalProjects.length > 0 ? internalProjects : projects;
    const selectedProject = allProjects.find((p) => p.id === projectId);
    setSelectedProjectName(
      selectedProject?.projectName || selectedProject?.name || "",
    );
  };

  /**
   * Handle task selection change
   * @param {React.ChangeEvent} e - Change event
   */
  const handleTaskChange = (e) => {
    setSelectedTaskId(e.target.value);
  };

  /**
   * Process selected files
   * @param {FileList|Array} fileList - Files to process
   */
  const processFiles = useCallback(
    (fileList) => {
      const newFiles = Array.from(fileList);

      // Check max files limit
      if (files.length + newFiles.length > MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files allowed per upload.`);
        return;
      }

      const processedFiles = [];

      newFiles.forEach((file) => {
        const validation = validateImageFile(file);

        if (validation.isValid) {
          // Create preview URL
          const previewUrl = URL.createObjectURL(file);

          processedFiles.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            previewUrl,
            status: UploadStatus.PENDING,
            progress: 0,
            error: null,
            cloudinaryUrl: null,
          });
        } else {
          setError(validation.error);
        }
      });

      if (processedFiles.length > 0) {
        setFiles((prev) => [...prev, ...processedFiles]);
        setError("");
      }
    },
    [files.length],
  );

  /**
   * Handle file input change
   * @param {React.ChangeEvent} e - Change event
   */
  const handleFileChange = (e) => {
    if (e.target.files?.length > 0) {
      processFiles(e.target.files);
    }
    // Reset input to allow selecting same file again
    e.target.value = "";
  };

  /**
   * Handle drag over event
   * @param {React.DragEvent} e - Drag event
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  /**
   * Handle drag leave event
   * @param {React.DragEvent} e - Drag event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  /**
   * Handle file drop
   * @param {React.DragEvent} e - Drop event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files?.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  /**
   * Remove a file from the upload list
   * @param {string} fileId - ID of file to remove
   */
  const removeFile = (fileId) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  /**
   * Upload all files
   */
  const handleUpload = async () => {
    // Validate project selection
    if (!selectedProjectId) {
      setError("Please select a project.");
      return;
    }

    // Validate files
    if (files.length === 0) {
      setError("Please select at least one photo to upload.");
      return;
    }

    setIsUploading(true);
    setError("");
    setUploadResults([]);

    // Start Sentry span for performance tracking
    return Sentry.startSpan(
      {
        op: "upload.progress_photos",
        name: "Upload Progress Photos",
      },
      async () => {
        try {
          const results = [];
          let completedCount = 0;

          // Upload each file to Cloudinary
          for (const fileItem of files) {
            try {
              // Update status to uploading
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id
                    ? { ...f, status: UploadStatus.UPLOADING }
                    : f,
                ),
              );

              // Build standardized Cloudinary context for this upload
              const uploadContext = buildUploadContext({
                projectId: selectedProjectId,
                projectName: selectedProjectName,
                category: "progress_photo",
                categoryLabel: "Progress Photo",
                sourceTable: "files_photos",
                uploadedByName: teamMemberName,
                extras: {
                  phase: selectedPhase || "",
                  task_id: selectedTaskId || "",
                  photo_name: photoName || "",
                },
                file: fileItem.file,
              });

              // Upload to Cloudinary
              const cloudinaryResult = await uploadFile(fileItem.file, {
                projectId: selectedProjectId,
                sourceTable: "files_photos",
                category: "progress_photo",
                uploadContext,
              });

              // Update file with Cloudinary URL
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id
                    ? {
                        ...f,
                        status: UploadStatus.SUCCESS,
                        progress: 100,
                        cloudinaryUrl: cloudinaryResult.secure_url,
                      }
                    : f,
                ),
              );

              results.push({
                fileName: fileItem.name,
                url: cloudinaryResult.url,
                publicId: cloudinaryResult.publicId,
              });

              completedCount++;
              setOverallProgress(
                Math.round((completedCount / files.length) * 100),
              );
            } catch (uploadErr) {
              console.error("Error uploading file:", fileItem.name, uploadErr);
              Sentry.captureException(uploadErr);

              // Mark file as failed
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileItem.id
                    ? {
                        ...f,
                        status: UploadStatus.ERROR,
                        error: uploadErr.message || "Upload failed",
                      }
                    : f,
                ),
              );

              completedCount++;
              setOverallProgress(
                Math.round((completedCount / files.length) * 100),
              );
            }
          }

          // If no files uploaded successfully, show error
          if (results.length === 0) {
            throw new Error("No files were uploaded successfully.");
          }

          // Create records in Airtable via Firebase function
          const createProgressPhotoBatch = httpsCallable(
            functions,
            "createProgressPhotoBatch",
          );

          const batchResult = await createProgressPhotoBatch({
            projectId: selectedProjectId,
            taskId: selectedTaskId || null,
            phase: selectedPhase, // NEW: Construction phase
            photos: results.map((r, index) => {
              // Determine fileName based on naming mode
              let displayName;
              if (individualNames) {
                // Use individual name if provided
                const fileId = files.find((f) => f.name === r.fileName)?.id;
                displayName = fileNames[fileId] || photoName;
              } else {
                // Use shared name with numbering if multiple files
                displayName =
                  results.length > 1 ? `${photoName} ${index + 1}` : photoName;
              }

              return {
                fileName: displayName, // NEW: Use determined name
                url: r.url,
                filename: r.fileName,
              };
            }),
            notes: notes.trim() || null,
          });

          if (!batchResult.data?.success) {
            throw new Error(
              batchResult.data?.error || "Failed to save photo records",
            );
          }

          // Success!
          setUploadComplete(true);
          setUploadResults(batchResult.data.data || results);

          // Call completion callback
          if (onUploadComplete) {
            onUploadComplete(batchResult.data.data);
          }
        } catch (err) {
          console.error("Upload error:", err);
          Sentry.captureException(err);
          setError(err.message || "An error occurred during upload.");
        } finally {
          setIsUploading(false);
        }
      },
    );
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    // Clean up preview URLs
    files.forEach((f) => {
      if (f.previewUrl) {
        URL.revokeObjectURL(f.previewUrl);
      }
    });
    onClose();
  };

  /**
   * Handle individual file name change
   * @param {string} fileId - File ID
   * @param {string} name - New name
   */
  const handleFileNameChange = (fileId, name) => {
    setFileNames((prev) => ({
      ...prev,
      [fileId]: name,
    }));
  };

  /**
   * Handle save in edit mode
   */
  const handleSaveEdit = async () => {
    console.log("[ProgressPhotoUploadModal] Saving edits...");
    setIsSaving(true);
    setError("");

    try {
      const updatePhoto = httpsCallable(functions, "updateProgressPhoto");
      const result = await updatePhoto({
        photoId: existingPhoto.id,
        updates: {
          fileName: photoName,
          phase: selectedPhase,
          notes: notes.trim() || null,
          taskId: selectedTaskId || null,
        },
      });

      console.log("[ProgressPhotoUploadModal] Photo updated successfully");

      // Close modal and notify parent
      onClose();
      if (onUploadComplete) {
        onUploadComplete(result.data?.data);
      }
    } catch (err) {
      console.error("[ProgressPhotoUploadModal] Save error:", err);
      Sentry.captureException(err);
      setError(err.message || "Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle delete confirmation
   */
  const handleDelete = async () => {
    console.log("[ProgressPhotoUploadModal] Deleting photo...");
    setIsDeleting(true);
    setError("");

    try {
      const deleteFn = httpsCallable(functions, "deleteFilesPhotosAttachment");
      await deleteFn({
        recordId: existingPhoto.id,
        attachmentId: existingPhoto.attachmentId || existingPhoto.id,
      });

      console.log("[ProgressPhotoUploadModal] Photo deleted successfully");

      // Close both modals
      setShowDeleteConfirmation(false);
      onClose();

      // Notify parent
      if (onDelete) {
        onDelete(existingPhoto.id);
      }
    } catch (err) {
      console.error("[ProgressPhotoUploadModal] Delete error:", err);
      Sentry.captureException(err);
      setError(err.message || "Failed to delete photo. Please try again.");
      setShowDeleteConfirmation(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // ==================== RENDER ====================

  if (!isOpen) return null;

  return (
    <div className="progress-photo-modal-overlay" onClick={handleClose}>
      <div
        className="progress-photo-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="progress-photo-modal-header">
          <div className="progress-photo-modal-header-left">
            <div className="progress-photo-modal-header-icon">
              {isEditMode ? <FaEdit /> : <FaUpload />}
            </div>
            <h3>
              {isEditMode ? "Edit Progress Photo" : "Upload Progress Photos"}
            </h3>
          </div>
          <button
            className="progress-photo-modal-close"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="progress-photo-modal-body">
          {uploadComplete ? (
            // Success State
            <div className="progress-photo-upload-success">
              <FaCheckCircle className="progress-photo-success-icon" />
              <h3>Upload Complete!</h3>
              <p>
                Successfully uploaded {uploadResults.length} photo
                {uploadResults.length !== 1 ? "s" : ""}.
              </p>
              <button className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Project and Phase - Side by Side */}
              <div className="progress-photo-form-row">
                {/* Project Selection */}
                <div className="progress-photo-form-section">
                  <label className="progress-photo-form-label">
                    <FaHome className="progress-photo-label-icon" />
                    Project <span className="required">*</span>
                  </label>
                  {isProjectLocked ? (
                    <div className="progress-photo-project-locked">
                      <span className="progress-photo-project-name">
                        {selectedProjectName || selectedProjectId}
                      </span>
                    </div>
                  ) : (
                    <select
                      className="progress-photo-form-select"
                      value={selectedProjectId}
                      onChange={handleProjectChange}
                      disabled={isUploading || projectsLoading}
                    >
                      <option value="">
                        {projectsLoading
                          ? "Loading projects..."
                          : "Select a project..."}
                      </option>
                      {(internalProjects.length > 0
                        ? internalProjects
                        : projects
                      ).map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.projectName || project.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Construction Phase Field */}
                <div className="progress-photo-form-section">
                  <label className="progress-photo-form-label">
                    <FaHardHat className="progress-photo-label-icon" />
                    Construction Phase <span className="required">*</span>
                  </label>
                  <select
                    className="progress-photo-form-select"
                    value={selectedPhase}
                    onChange={(e) => setSelectedPhase(e.target.value)}
                    disabled={isUploading || isSaving}
                    required
                  >
                    <option value="">Select phase...</option>
                    {CONSTRUCTION_PHASES.map((phase) => (
                      <option key={phase.value} value={phase.value}>
                        {phase.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Task Selection (Optional) */}
              <div className="progress-photo-form-section">
                <label className="progress-photo-form-label">
                  <FaTasks className="progress-photo-label-icon" />
                  Link to Task{" "}
                  <span className="progress-photo-optional">(optional)</span>
                </label>
                <select
                  className="progress-photo-form-select"
                  value={selectedTaskId}
                  onChange={handleTaskChange}
                  disabled={isUploading || !selectedProjectId || loadingTasks}
                >
                  <option value="">
                    {loadingTasks
                      ? "Loading tasks..."
                      : !selectedProjectId
                        ? "Select a project first"
                        : "No task selected"}
                  </option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.phase ? `[${task.phase}] ` : ""}
                      {task.taskName}
                    </option>
                  ))}
                </select>
                <p className="progress-photo-form-hint">
                  Optionally link these photos to a specific task
                </p>
              </div>

              {/* Photo Name Field (NEW - Required) */}
              <div className="progress-photo-form-section">
                <label className="progress-photo-form-label">
                  <FaEdit className="progress-photo-label-icon" />
                  Photo Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="progress-photo-form-input"
                  value={photoName}
                  onChange={(e) => setPhotoName(e.target.value)}
                  placeholder="e.g., Foundation Pour Complete"
                  disabled={isUploading || isSaving || individualNames}
                  required
                />
                {!isEditMode && files.length > 1 && (
                  <>
                    {individualNames ? (
                      <p className="progress-photo-form-hint">
                        Name each photo individually using the inputs below.
                      </p>
                    ) : (
                      <p className="progress-photo-form-hint">
                        Photos will be named: "{photoName} 1", "{photoName} 2",
                        etc.
                      </p>
                    )}
                    <div className="progress-photo-individual-checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="individual-names"
                        className="progress-photo-individual-checkbox"
                        checked={individualNames}
                        onChange={(e) => setIndividualNames(e.target.checked)}
                        disabled={isUploading}
                      />
                      <label htmlFor="individual-names">
                        Name photos individually
                      </label>
                    </div>
                  </>
                )}
              </div>

              {/* File Drop Zone (Create Mode Only) */}
              {!isEditMode && (
                <>
                  {/* File Preview Grid (Show Above Dropzone) */}
                  {files.length > 0 && (
                    <div className="progress-photo-preview-section">
                      <div className="progress-photo-preview-header">
                        <span>
                          {files.length} photo{files.length !== 1 ? "s" : ""}{" "}
                          selected
                        </span>
                        {!isUploading && (
                          <button
                            className="progress-photo-clear-all"
                            onClick={() => {
                              files.forEach((f) => {
                                if (f.previewUrl) {
                                  URL.revokeObjectURL(f.previewUrl);
                                }
                              });
                              setFiles([]);
                            }}
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                      <div className="progress-photo-preview-grid">
                        {files.map((fileItem) => (
                          <div
                            key={fileItem.id}
                            className={`progress-photo-preview-item ${
                              fileItem.status === UploadStatus.ERROR
                                ? "progress-photo-preview-error"
                                : ""
                            } ${
                              fileItem.status === UploadStatus.SUCCESS
                                ? "progress-photo-preview-success"
                                : ""
                            }`}
                          >
                            <div className="progress-photo-preview-image-wrapper">
                              <img
                                src={fileItem.previewUrl}
                                alt={fileItem.name}
                                className="progress-photo-preview-image"
                              />
                              {/* Status overlay */}
                              {fileItem.status === UploadStatus.UPLOADING && (
                                <div className="progress-photo-preview-overlay">
                                  <FaSpinner className="progress-photo-preview-spinner" />
                                </div>
                              )}
                              {fileItem.status === UploadStatus.SUCCESS && (
                                <div className="progress-photo-preview-overlay progress-photo-preview-overlay-success">
                                  <FaCheckCircle />
                                </div>
                              )}
                              {fileItem.status === UploadStatus.ERROR && (
                                <div className="progress-photo-preview-overlay progress-photo-preview-overlay-error">
                                  <FaExclamationCircle />
                                </div>
                              )}
                            </div>
                            <div className="progress-photo-preview-info">
                              <span className="progress-photo-preview-name">
                                {fileItem.name}
                              </span>
                              <span className="progress-photo-preview-size">
                                {formatFileSize(fileItem.size)}
                              </span>
                            </div>

                            {/* Individual Name Input (if enabled) */}
                            {individualNames && !isUploading && (
                              <input
                                type="text"
                                className="progress-photo-individual-name-input"
                                value={fileNames[fileItem.id] || ""}
                                onChange={(e) =>
                                  handleFileNameChange(
                                    fileItem.id,
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter photo name..."
                                required
                              />
                            )}

                            {!isUploading && (
                              <button
                                className="progress-photo-preview-remove"
                                onClick={() => removeFile(fileItem.id)}
                                aria-label={`Remove ${fileItem.name}`}
                              >
                                <FaTrash />
                              </button>
                            )}
                            {fileItem.error && (
                              <span className="progress-photo-preview-error-text">
                                {fileItem.error}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dropzone */}
                  <div className="progress-photo-form-section">
                    <label className="progress-photo-form-label">
                      <FaImage className="progress-photo-label-icon" />
                      Photos <span className="required">*</span>
                    </label>
                    <div
                      className={`progress-photo-dropzone ${
                        isDragging ? "progress-photo-dropzone-dragging" : ""
                      } ${isUploading ? "progress-photo-dropzone-disabled" : ""}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        id="photo-upload"
                        className="progress-photo-file-input"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        disabled={isUploading}
                      />
                      <label
                        htmlFor="photo-upload"
                        className="progress-photo-dropzone-content"
                      >
                        <FaCloudUploadAlt className="progress-photo-dropzone-icon" />
                        <p className="progress-photo-dropzone-text hide-sm">
                          Drag & drop photos here or click to browse
                        </p>{" "}
                        <p className="progress-photo-dropzone-text show-sm">
                          Upload a photo or take a picture from your device
                        </p>
                        <p className="progress-photo-dropzone-hint">
                          JPG, PNG, WebP • Max 10MB per file • Up to {MAX_FILES}{" "}
                          files
                        </p>
                      </label>
                      <div className="progress-photo-mobile-actions">
                        <input
                          type="file"
                          id="photo-upload-gallery"
                          className="progress-photo-mobile-input"
                          accept="image/*"
                          multiple
                          onChange={handleFileChange}
                          disabled={isUploading}
                        />
                        <label
                          htmlFor="photo-upload-gallery"
                          className="progress-photo-mobile-btn progress-photo-mobile-btn-primary"
                        >
                          <FaImage />
                          Browse Photos
                        </label>
                        <input
                          type="file"
                          id="photo-upload-camera"
                          className="progress-photo-mobile-input"
                          accept="image/*"
                          capture="environment"
                          onChange={handleFileChange}
                          disabled={isUploading}
                        />
                        <label
                          htmlFor="photo-upload-camera"
                          className="progress-photo-mobile-btn progress-photo-mobile-btn-secondary"
                        >
                          <FaCamera />
                          Take Photo
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Existing Photo Preview (Edit Mode Only) */}
              {isEditMode && existingPhoto && (
                <div className="progress-photo-form-section">
                  <label className="progress-photo-form-label">
                    <FaImage className="progress-photo-label-icon" />
                    Current Photo
                  </label>
                  <div className="progress-photo-existing-preview">
                    {existingPhoto.attachments &&
                    existingPhoto.attachments.length > 0 ? (
                      <img
                        src={existingPhoto.attachments[0].url}
                        alt={existingPhoto.fileName}
                        className="progress-photo-existing-image"
                      />
                    ) : (
                      <div className="progress-photo-existing-placeholder">
                        <FaImage />
                        <span>No image available</span>
                      </div>
                    )}
                  </div>
                  <p className="progress-photo-form-hint">
                    Photo attachments cannot be changed in edit mode
                  </p>
                </div>
              )}

              {/* Notes Field */}
              <div className="progress-photo-form-section">
                <label className="progress-photo-form-label">
                  <FaStickyNote className="progress-photo-label-icon" /> Notes{" "}
                  <span className="progress-photo-optional">(optional)</span>
                </label>
                <textarea
                  className="progress-photo-form-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes or description for these photos..."
                  rows={3}
                  disabled={isUploading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="progress-photo-error">
                  <FaExclamationCircle />
                  <span>{error}</span>
                </div>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="progress-photo-progress-section">
                  <div className="progress-photo-progress-bar-container">
                    <div
                      className="progress-photo-progress-bar"
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                  <span className="progress-photo-progress-text">
                    Uploading... {overallProgress}%
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!uploadComplete && (
          <div className="progress-photo-modal-footer">
            {isEditMode ? (
              // Edit Mode Footer
              <>
                <button
                  className="progress-photo-btn-delete"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={isDeleting || isSaving}
                >
                  <FaTrash />
                  Delete Photo
                </button>
                <div className="progress-photo-modal-footer-right">
                  <button
                    className="progress-photo-btn-cancel"
                    onClick={handleClose}
                    disabled={isDeleting || isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="progress-photo-btn-save"
                    onClick={handleSaveEdit}
                    disabled={
                      isSaving ||
                      isDeleting ||
                      !photoName.trim() ||
                      !selectedPhase
                    }
                  >
                    {isSaving ? (
                      <>
                        <FaSpinner className="progress-photo-btn-spinner" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              // Create Mode Footer
              <>
                <button
                  className="progress-photo-btn-cancel"
                  onClick={handleClose}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  className="progress-photo-btn-upload"
                  onClick={handleUpload}
                  disabled={
                    isUploading ||
                    files.length === 0 ||
                    !selectedProjectId ||
                    !selectedPhase ||
                    (individualNames
                      ? files.some((f) => !fileNames[f.id]?.trim())
                      : !photoName.trim())
                  }
                >
                  {isUploading ? (
                    <>
                      <FaSpinner className="progress-photo-btn-spinner" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload />
                      Upload {files.length > 0 ? `${files.length} ` : ""}Photo
                      {files.length !== 1 ? "s" : ""}
                      {!isProjectLocked && selectedProjectName
                        ? ` to ${selectedProjectName}`
                        : ""}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDelete}
          title="Delete Progress Photo?"
          message={`This will permanently delete "${existingPhoto?.fileName || "this photo"}".`}
          confirmText="Delete"
          isDestructive={true}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default ProgressPhotoUploadModal;
